/**
 * 用户相关 API
 */
import { createHttpInstance, createFullHttpInstance } from './http'
import { BASE_URL, API_PATH } from './config'

// 创建认证服务 HTTP 实例
const authHttp = createHttpInstance(BASE_URL.AUTH)
// 创建机构服务 HTTP 实例（使用完整 URL）
const orgHttp = createFullHttpInstance()
// 创建超级管理员 HTTP 实例
const adminHttp = createHttpInstance(BASE_URL.ADMIN)

/**
 * 用户登录
 * @param {Object} data
 * @param {number} data.type - 1:密码登录, 2:验证码登录
 * @param {string} data.mobile - 手机号
 * @param {string} data.password - 密码（密码登录时传）
 * @param {string} data.captcha_mobile - 验证码（验证码登录时传）
 */
export function login(data) {
  return authHttp.post(API_PATH.LOGIN, data)
}

/**
 * 获取机构列表
 * @param {Object} params
 * @param {number} params.page - 当前页
 * @param {number} params.limit - 每页条数
 * @param {string} params.keyword - 搜索关键词
 */
export function getOrganizationList(params) {
  return orgHttp.post(BASE_URL.ORG + API_PATH.ORG_LIST, params)
}

/**
 * 发送短信验证码
 * @param {Object} data
 * @param {string} data.mobile - 手机号
 * @param {string} data.event - 事件类型：register(注册) / login(登录)
 */
export function sendSms(data) {
  return orgHttp.post(BASE_URL.ORG + API_PATH.SEND_SMS, data)
}

/**
 * 用户注册
 * @param {Object} data
 * @param {string} data.mobile - 手机号
 * @param {string} data.captcha_mobile - 手机验证码
 * @param {string} data.password - 密码
 * @param {string} data.instname - 机构名称
 * @param {string} data.instid - 统一社会信用代码
 * @param {string} data.nickname - 姓名
 * @param {string} data.posid - 岗位ID
 */
export function register(data) {
  return authHttp.post(API_PATH.REGISTER, data)
}

/**
 * 修改密码
 * @param {Object} data
 * @param {string} data.oldpassword - 老密码
 * @param {string} data.newpassword - 新密码
 * @param {string} data.renewpassword - 确认新密码
 */
export function changePassword(data) {
  return authHttp.post(API_PATH.CHANGE_PASSWORD, data)
}

/**
 * 获取用户信息
 * @returns {Promise}
 */
export function getMemberInfo() {
  return authHttp.post(API_PATH.GET_MEMBER_INFO)
}

/**
 * 退出登录
 * @returns {Promise}
 */
export function logout() {
  return authHttp.post(API_PATH.LOGOUT)
}

/**
 * 获取岗位类型列表
 * @returns {Promise}
 */
export function getPosType() {
  return orgHttp.post(BASE_URL.ORG + API_PATH.GET_POS_TYPE)
}

/**
 * 编辑个人信息（从业人员）
 * @param {Object} data
 * @param {string} data.nickname - 姓名
 * @returns {Promise}
 */
export function updateProfile(data) {
  return authHttp.post(API_PATH.UPDATE_PROFILE, data)
}

/**
 * 获取数据填报时间列表（从业人员端）
 * @returns {Promise}
 */
export function getFillTimeData() {
  return authHttp.post(API_PATH.GET_FILL_TIME_DATA)
}

/**
 * 获取首页统计数据
 * @returns {Promise}
 */
export function getIndexData() {
  return orgHttp.post(BASE_URL.ORG + API_PATH.GET_INDEX_DATA)
}

/**
 * 获取地区数据
 * @param {Object} params
 * @param {number} params.pid - 父级ID，0表示获取省份
 */
export function getAreaData(params) {
  return orgHttp.post(BASE_URL.ORG + API_PATH.GET_AREA_DATA, params)
}

/**
 * 超级管理员登录
 * @param {Object} data
 * @param {string} data.username - 用户名
 * @param {string} data.password - 密码
 */
export function superAdminLogin(data) {
  return adminHttp.post(API_PATH.SUPER_ADMIN_LOGIN, data)
}

/**
 * 超级管理员退出登录
 */
export function superAdminLogout() {
  return adminHttp.post(API_PATH.SUPER_ADMIN_LOGOUT)
}

/**
 * 获取用户列表
 * @param {Object} params
 * @param {number} params.page - 当前页
 * @param {number} params.limit - 每页条数
 * @param {string} params.keyword - 搜索关键词
 * @param {string} params.role - 角色
 * @param {string} params.status - 状态
 */
export function getUserList(params) {
  return adminHttp.post(API_PATH.GET_USER_LIST, params)
}

/**
 * 冻结/解冻用户
 * @param {Object} data
 * @param {number} data.id - 用户ID
 */
export function freezeUser(data) {
  return adminHttp.post(API_PATH.FREEZE_USER, data)
}

/**
 * 删除用户
 * @param {Object} data
 * @param {number} data.id - 用户ID
 */
export function deleteUser(data) {
  return adminHttp.post(API_PATH.DELETE_USER, data)
}

/**
 * 创建管理员
 * @param {Object} data
 * @param {string} data.nickname - 姓名
 * @param {string} data.mobile - 手机号
 * @param {string} data.password - 密码
 */
export function createAdmin(data) {
  return adminHttp.post(API_PATH.CREATE_ADMIN, data)
}

/**
 * 重置用户密码
 * @param {Object} data
 * @param {number} data.id - 用户ID
 */
export function resetPassword(data) {
  return adminHttp.post(API_PATH.RESET_PASSWORD, data)
}

/**
 * 获取填报时间列表
 */
export function getFillTimeList() {
  return adminHttp.post(API_PATH.GET_FILL_TIME_LIST)
}

/**
 * 添加填报时间
 * @param {Object} data
 * @param {string} data.title - 填报时间名称
 * @param {string} data.start_time - 开始时间
 * @param {string} data.end_time - 结束时间
 */
export function addFillTime(data) {
  return adminHttp.post(API_PATH.ADD_FILL_TIME, data)
}

/**
 * 获取招聘岗位列表
 * @param {Object} params
 * @param {string} params.page - 当前页
 * @param {string} params.limit - 每页条数
 * @param {string} [params.city] - 城市
 * @param {string} [params.stime] - 更新时间-开始
 * @param {string} [params.etime] - 更新时间-结束
 * @param {string} params.keyword - 搜索关键词
 */
export function getJobList(params) {
  return orgHttp.post(BASE_URL.ORG + API_PATH.GET_JOB_LIST, params)
}

/**
 * 获取招聘岗位详情
 * @param {Object} params
 * @param {number} params.id - 岗位 ID
 */
export function getJobDetail(params) {
  return orgHttp.post(BASE_URL.ORG + API_PATH.GET_JOB_DETAIL, params)
}

/**
 * 获取国家标准列表
 * @param {Object} params
 * @param {number} params.page - 当前页
 * @param {number} params.limit - 每页条数
 */
export function getNationList(params) {
  return authHttp.post(API_PATH.GET_NATION_LIST, params)
}

/**
 * 获取地方标准/团体标准列表
 * @param {Object} params
 * @param {number} params.page - 当前页
 * @param {number} params.limit - 每页条数
 * @param {string} params.type - 标准类型：地方标准/团体标准
 * @param {string} [params.city] - 城市
 * @param {string} [params.keyword] - 搜索关键词
 */
export function getAllStandardList(params) {
  return authHttp.post(API_PATH.GET_ALL_STANDARD_LIST, params)
}

/**
 * 查看国家标准详情
 * @param {Object} params
 * @param {number} params.id - 标准 ID
 */
export function viewNationInfo(params) {
  return authHttp.post(API_PATH.VIEW_NATION_INFO, params)
}

/**
 * 查看地方标准/团体标准详情
 * @param {Object} params
 * @param {number} params.id - 标准 ID
 */
export function viewStandard(params) {
  return authHttp.post(API_PATH.VIEW_STANDARD, params)
}

/**
 * 下载地方标准/团体标准
 * @param {Object} params
 * @param {number} params.id - 标准 ID
 */
export function downloadStandard(params) {
  return authHttp.post(API_PATH.DOWNLOAD_STANDARD, params, {
    responseType: 'blob',
  })
}

/**
 * 获取我的标准列表
 * @param {Object} params
 * @param {number} params.page - 当前页
 * @param {number} params.limit - 每页条数
 * @param {string} [params.city] - 城市
 * @param {string} [params.keyword] - 搜索关键词
 */
export function getMyStandard(params) {
  return authHttp.post(API_PATH.GET_MY_STANDARD, params)
}

/**
 * 上传标准
 * @param {Object} data
 * @param {string} data.title - 标准名称
 * @param {string} data.type - 标准类型
 * @param {string} data.standno - 标准号
 * @param {string} data.prov - 省份
 * @param {string} data.city - 城市
 * @param {string} data.county - 区县
 * @param {string} data.rel_date - 发布日期
 * @param {string} data.eff_date - 实施日期
 * @param {string} data.unitname - 起草单位
 * @param {string} data.drafter - 起草人
 * @param {string} data.files - 文档路径
 * @param {number} data.status - 上传状态：1-提交；0-保存
 */
export function addStandData(data) {
  return authHttp.post(API_PATH.ADD_STAND_DATA, data)
}

/**
 * 上传文件
 * @param {FormData} formData - 包含文件的 FormData 对象
 */
export function uploadFile(formData) {
  return orgHttp.post(BASE_URL.ORG + API_PATH.UPLOAD_FILE, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

/**
 * 删除我的标准
 * @param {Object} data
 * @param {number} data.id - 标准 ID
 */
export function deleteStandard(data) {
  return authHttp.post(API_PATH.DELETE_STANDARD, data)
}

/**
 * 提交/编辑标准
 * @param {Object} data
 * @param {number} [data.id] - 标准 ID（编辑时传入）
 * @param {string} data.title - 标准名称
 * @param {string} data.type - 标准类型
 * @param {string} data.standno - 标准号
 * @param {string} data.prov - 省份
 * @param {string} data.city - 城市
 * @param {string} data.county - 区县
 * @param {string} data.rel_date - 发布日期
 * @param {string} data.eff_date - 实施日期
 * @param {string} data.unitname - 起草单位
 * @param {string} data.drafter - 起草人
 * @param {string} data.files - 文档路径
 * @param {number} data.status - 上传状态：1-提交；0-保存
 */
export function submitStandard(data) {
  return authHttp.post(API_PATH.SUBMIT_STANDARD, data)
}

/**
 * 获取政策法规分类列表
 * @param {Object} params
 * @param {number} params.catid - 分类ID（国家政策: 4; 地方政策: 5）
 */
export function getCateList(params) {
  return authHttp.post(API_PATH.GET_CATE_LIST, params)
}

/**
 * 获取国家政策列表
 * @param {Object} params
 * @param {number} params.page - 当前页
 * @param {number} params.limit - 每页条数
 * @param {number} params.catid - 分类ID（可选）
 * @param {string} params.keyword - 搜索关键词（可选）
 */
export function getNatPolList(params) {
  return authHttp.post(API_PATH.GET_NAT_POL_LIST, params)
}

/**
 * 获取地方政策列表
 * @param {Object} params
 * @param {number} params.page - 当前页
 * @param {number} params.limit - 每页条数
 * @param {number} params.catid - 分类ID（可选）
 * @param {string} params.keyword - 搜索关键词（可选）
 */
export function getLocalPolList(params) {
  return authHttp.post(API_PATH.GET_LOCAL_POL_LIST, params)
}

/**
 * 查看政策详情
 * @param {Object} params
 * @param {string} params.id - 政策ID
 */
export function viewPolicyInfo(params) {
  return authHttp.post(API_PATH.VIEW_POLICY_INFO, params)
}

/**
 * 管理员端整体概览
 * @param {Object} params
 * @param {string} params.stime - 更新时间-开始
 * @param {string} params.etime - 更新时间-结束
 * @param {string} params.city - 城市
 */
export function getDataOverview(params) {
  return authHttp.post(API_PATH.DATA_OVERVIEW, params)
}

/**
 * 管理员端机构概览
 * @param {Object} params
 * @param {string} params.stime - 更新时间-开始
 * @param {string} params.etime - 更新时间-结束
 * @param {string} params.city - 城市
 */
export function getOrgOverview(params) {
  return authHttp.post(API_PATH.ORG_OVERVIEW, params)
}

/**
 * 管理员端岗位需求趋势
 * @param {Object} params
 * @param {string} params.stime - 更新时间-开始
 * @param {string} params.etime - 更新时间-结束
 * @param {string} params.city - 城市
 */
export function getJobTrend(params) {
  return authHttp.post(API_PATH.JOB_TREND, params)
}

/**
 * 管理员端Top 5热门岗位
 * @param {Object} params
 * @param {string} params.stime - 更新时间-开始
 * @param {string} params.etime - 更新时间-结束
 * @param {string} params.city - 城市
 */
export function getTopJobs(params) {
  return authHttp.post(API_PATH.TOP_JOBS, params)
}

/**
 * 管理员端岗位学历和证书要求
 * @param {Object} params
 * @param {string} params.stime - 更新时间-开始
 * @param {string} params.etime - 更新时间-结束
 * @param {string} params.city - 城市
 * @param {string} params.position - 岗位
 */
export function getJobRequirements(params) {
  return authHttp.post(API_PATH.JOB_REQUIREMENTS, params)
}

/**
 * 管理员端从业人员概览
 * @param {Object} params
 * @param {string} params.stime - 更新时间-开始
 * @param {string} params.etime - 更新时间-结束
 * @param {string} params.city - 城市
 * @param {string} params.position - 岗位
 */
export function getPractitionerOverview(params) {
  return authHttp.post(API_PATH.PRACTITIONER_OVERVIEW, params)
}

/**
 * 管理员端从业人员能力评估
 * @param {Object} params
 * @param {string} params.stime - 更新时间-开始
 * @param {string} params.etime - 更新时间-结束
 * @param {string} params.city - 城市
 * @param {string} params.position - 岗位
 */
export function getPractitionerCapability(params) {
  return authHttp.post(API_PATH.PRACTITIONER_CAPABILITY, params)
}

/**
 * 获取填报列表（从业人员端）
 * @param {Object} params
 * @param {number} params.page - 当前页
 * @param {number} params.limit - 每页条数
 * @param {number} params.posid - 岗位ID（可选）
 * @param {string} params.instid - 所在地区（可选）
 */
export function getSurveyList(params) {
  return orgHttp.post(BASE_URL.ORG + API_PATH.GET_SURVEY_LIST, params)
}

/**
 * 获取机构数据列表（管理员端）
 * @param {Object} params
 * @param {number} params.page - 当前页
 * @param {number} params.limit - 每页条数
 * @param {string} params.city - 城市（可选）
 * @param {string} params.stime - 更新时间-开始（可选）
 * @param {string} params.etime - 更新时间-结束（可选）
 * @param {string} params.keyword - 搜索关键词（可选）
 * @param {string} params.org_type - 机构举办主体（可选）
 * @param {string} params.org_form - 机构具体形态（可选）
 */
export function getSurveyDataList(params) {
  return authHttp.post(API_PATH.GET_SURVEY_DATA_LIST, params)
}

/**
 * 初始化填报（从业人员端）
 * @param {Object} data
 * @param {number} data.fid - 填报时间批次ID
 */
export function initSurvey(data) {
  return orgHttp.post(BASE_URL.ORG + API_PATH.INIT_SURVEY, data)
}

/**
 * 获取填报进度
 * @param {Object} data
 * @param {number} data.fid - 填报时间批次ID
 */
export function getProgress(data) {
  return orgHttp.post(BASE_URL.ORG + API_PATH.GET_PROGRESS, data)
}

/**
 * 查看填报详情
 * @param {Object} data
 * @param {number} data.id - 填报记录ID
 */
export function viewSurvey(data) {
  return orgHttp.post(BASE_URL.ORG + API_PATH.VIEW_SURVEY, data)
}

/**
 * 保存步骤数据 / 提交填报（从业人员端）
 * @param {Object} data
 * @param {string} data.survey_id - 填报 ID
 * @param {number} data.step - 步骤编号（1-9）
 * @param {number} [data.is_submit] - 是否提交：1=提交，0=保存草稿
 * @param {*} data - 其余为各步骤字段
 */
export function saveStep(data) {
  return orgHttp.post(BASE_URL.ORG + API_PATH.SAVE_STEP, data)
}

/**
 * 获取托育机构列表（管理员端 - 托育机构地图）
 * @param {Object} params
 * @param {number} params.page - 当前页
 * @param {number} params.limit - 每页条数
 * @param {string} [params.type] - 机构性质（可选）
 * @param {string} [params.city] - 城市名（可选）
 * @param {string} [params.keyword] - 搜索关键词（可选）
 */
export function getTuoyuList(params) {
  return orgHttp.post(BASE_URL.ORG + '/apiparam/getTuoyuList', params)
}
